"use client";

import { useEffect, useRef } from "react";
import AppImage from "@/components/ui/AppImage";
import Icon from "@/components/ui/AppIcon";

const testimonials = [
{
  quote:
  "Client no-shows dropped from about 22% to under 8% in the first two months. The automated reminders do the work we used to do manually with phone calls. Our front desk staff have genuinely thanked us.",
  name: "Dr. Amara Osei",
  role: "Lead Veterinarian",
  business: "Greenwood Animal Hospital, Brooklyn NY",
  avatar:
  "https://img.rocket.new/generatedImages/rocket_gen_img_13e1df70a-1763301872470.png",
  avatarAlt:
  "Dr. Amara Osei, professional woman in a medical coat, lead veterinarian at Greenwood Animal Hospital",
  metric: "No-shows ↓ 64%",
  metricColor: "#0EA5A0"
},
{
  quote:
  "As a solo groomer I was losing clients who just forgot to rebook. Now DogFob sends them a nudge when their dogfob is due for a trim based on breed. My rebooking rate went from 55% to 81% in three months.",
  name: "Sofia Reyes",
  role: "Owner & Groomer",
  business: "PawSpa Mobile Grooming, Denver CO",
  avatar:
  "https://img.rocket.new/generatedImages/rocket_gen_img_1d64f156d-1764932242254.png",
  avatarAlt:
  "Sofia Reyes, young woman with curly hair smiling, owner of PawSpa Mobile Grooming in Denver",
  metric: "Rebook rate +47%",
  metricColor: "#7C3AED"
},
{
  quote:
  "We run a 24-dogfob boarding facility. Before DogFob, check-in took 15 minutes per dogfob because owners had to fill in dietary and medical forms. Now the profile loads in seconds. We've cut check-in time by 80%.",
  name: "David Okafor",
  role: "Operations Manager",
  business: "TailWagg Boarding Resort, Atlanta GA",
  avatar:
  "https://img.rocket.new/generatedImages/rocket_gen_img_1cd063d3a-1768495177064.png",
  avatarAlt:
  "David Okafor, professional man in a casual shirt, operations manager at TailWagg Boarding Resort",
  metric: "Check-in time ↓ 80%",
  metricColor: "#D97706"
}];


export default function BusinessTestimonials() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.
            querySelectorAll(".scroll-fade-up, .scroll-scale").
            forEach((el, i) => {
              setTimeout(() => el.classList.add("is-visible"), i * 100);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="py-24 relative overflow-hidden"
      style={{ background: "linear-gradient(145deg, #0F2027 0%, #1E3A3A 50%, #0F2027 100%)" }}>
      
      <div
        className="absolute top-[-20%] right-[-10%] w-[500px] h-[500px] rounded-full pointer-events-none"
        style={{
          background: "radial-gradient(circle, rgba(14,165,160,0.1) 0%, transparent 70%)"
        }} />
      
      <div className="max-w-7xl mx-auto px-5 sm:px-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-14 scroll-fade-up">
          <span
            className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest px-3 py-1.5 rounded-full mb-4"
            style={{ background: "rgba(14,165,160,0.15)", color: "#5CCFCB" }}>
            
            <Icon name="ChatBubbleLeftRightIcon" size={12} />
            Partner Stories
          </span>
          <h2 className="text-4xl sm:text-5xl font-extrabold text-white tracking-tight mt-4 mb-4">
            Businesses that grow with DogFob
          </h2>
          <p className="text-[#8AA4A4] text-lg max-w-lg mx-auto">
            Real results from our 180+ verified partner businesses across the US.
          </p>
        </div>

        {/* Cards */}
        <div className="grid md:grid-cols-3 gap-6">
          {testimonials?.map((t, idx) =>
          <div
            key={`biz-t-${idx}`}
            className={`scroll-scale stagger-${idx + 1} glass-card-dark rounded-3xl p-7 flex flex-col gap-5 hover:border-teal-500/25 transition-all duration-300 hover:scale-[1.02] hover:-translate-y-1 hover:shadow-xl`}>
            
              {/* Stars */}
              <div className="flex items-center gap-1">
                {Array.from({ length: 5 })?.map((_, si) =>
              <Icon
                key={si}
                name="StarIcon"
                size={13}
                style={{ color: "#FBBF24" }}
                variant="solid" />

              )}
              </div>

              <blockquote className="text-[#D1D5DB] leading-relaxed text-sm flex-1">
                "{t?.quote}"
              </blockquote>

              {/* Metric badge */}
              <div
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold w-fit"
              style={{ background: `${t?.metricColor}18`, color: t?.metricColor }}>
              
                <Icon name="ArrowTrendingUpIcon" size={12} />
                {t?.metric}
              </div>

              {/* Author */}
              <div className="flex items-center gap-3 pt-3 border-t border-white/10">
                <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                  <AppImage
                  src={t?.avatar}
                  alt={t?.avatarAlt}
                  width={40}
                  height={40}
                  className="w-full h-full object-cover" />
                
                </div>
                <div>
                  <p className="text-sm font-bold text-white font-heading">{t?.name}</p>
                  <p className="text-[10px] text-[#8AA4A4]">{t?.role}</p>
                  <p className="text-[10px] text-teal-400">{t?.business}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>);

}